import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { ProviderCard } from "@/components/search/ProviderCard";
import {
 citySlug,
 specialtySlug,
 resolveCityFromSlug,
 resolveSpecialtyFromSlug,
 SEO_INSURANCE_IDS,
 SEO_SPECIALTIES,
 SEO_CITIES,
 stateFromNameSlug,
 stateNameSlug,
} from "@/lib/seo-slugs";
import { INSURANCES, searchProviders, type Provider } from "@/data/heallexa";
import { providerSchemaType } from "@/lib/seo-schema";
import { buildProviderSlug } from "@/lib/provider-slug";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import {
   CITY_FACTS,
   buildIntroParagraph,
   buildFaqs,
   pickNearbyCities,
   specialtyPlural,
   specialtyPluralLower,
   specialtyWithArticle,
   isLandingCity,
   buildSpecialtyCityMeta,
 } from "@/lib/specialty-city-enrichment";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialty/$specialty/$city")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setSeoPageCache();
 const specialty = resolveSpecialtyFromSlug(params.specialty);
 const city = resolveCityFromSlug(params.city);
 // ponytail: curated cities are already "Dallas, TX" (include state); for unknown city slugs
 // try CITY_FACTS for state, else fall back to city-only (slow path but rare).
 const locationArg = city.includes(",")
   ? city
   : (CITY_FACTS[city]?.state ? `${city}, ${CITY_FACTS[city].state}` : city);
 let providers: Provider[] = [];
 try {
  // Strict AND: specialty pattern (e.g. cardiolog|cardiovascular) AND
  // city=Dallas AND state=TX are joined as .eq().eq() inside the RPC.
  // Limit 100 to expose true depth on cities like Dallas, TX where we
  // have hundreds of matches.
  providers = await searchProviders({ specialty, location: locationArg, limit: 100 });
 } catch {
 providers = [];
 }
 // Soft 404 fix: 0-provider specialty+city combos used to render a thin
 // "no providers yet, here are nearby cities" page with a 200 status and
 // noindex. Google classifies that as a soft 404 and keeps re-crawling it
 // anyway, burning crawl budget across the highest-traffic programmatic
 // route on the site. A real 404 lets Google deindex cleanly; the
 // specialty+city combo can be re-crawled once providers exist for it.
 if (providers.length === 0) throw notFound();
 return { specialty, city, providers };
 },
 head: ({ params, loaderData }) => {
 const specialty = loaderData?.specialty ?? "Providers";
 const city = loaderData?.city ?? "United States";
 const providers = loaderData?.providers ?? [];
 const facts = CITY_FACTS[city];
 const cityShort = facts?.short ?? city.split(",")[0];
 const stateShort = facts?.state ?? (city.split(",")[1] ?? "").trim();
 const count = providers.length;

 const nearbyCitiesForRobots = pickNearbyCities({
   specialty,
   currentCity: city,
   allCities: SEO_CITIES,
   count: 10,
 });
 const faqsForRobots = buildFaqs({ specialty, city });
 const relatedSpecialtiesCount = Math.min(
   SEO_SPECIALTIES.filter((s) => s !== specialty).length,
   16,
 );
 // Real internal-link count actually rendered on the page: nearby-city
 // chips + related-specialty chips + insurance-by-city chips + the
 // specialty/state hub links in the header/CTA area.
 const internalLinkCount =
   nearbyCitiesForRobots.length + relatedSpecialtiesCount + SEO_INSURANCE_IDS.length + 4;

 // Indexability: thin pages (1-2 providers) only stay indexed when the page
 // has real enrichment. This used to be bypassed by hardcoding hasFaq and
 // hasNearbyCities to `true` and internalLinkCount to 30 regardless of the
 // page's actual content — every thin page passed the "enriched" check
 // trivially, so large numbers of near-empty specialty+city pages got
 // indexed anyway, dragging on site-wide quality signals. Empty pages (0
 // providers) no longer reach here at all — the loader throws notFound().
 const robots = decideIndexability({
   providerCount: count,
   hasFaq: faqsForRobots.length > 0,
   hasNearbyCities: nearbyCitiesForRobots.length > 0,
   hasRelatedProviders: count > 0,
   internalLinkCount,
 });
 const url = `${SITE}/specialty/${params.specialty}/${params.city}`;
 const stateFullSlug = stateFromNameSlug(stateShort)?.name ? stateNameSlug(stateFromNameSlug(stateShort)!.name) : (stateShort || "").toLowerCase();
 const baseUrl = `${SITE}/specialty/${params.specialty}/doctors/${stateFullSlug}`;
 const canonical = robots.index ? url : baseUrl;

  const meta = buildSpecialtyCityMeta({
    specialty,
    city,
    providerCount: count,
    nearbyCount: 10,
    faqCount: 4,
  });
  const title = meta.title;
  const description = meta.description;

 const itemList = {
 "@context": "https://schema.org",
 "@type": "ItemList",
 itemListOrder: "https://schema.org/ItemListOrderDescending",
 numberOfItems: providers.length,
 itemListElement: providers.slice(0, 25).map((p, i) => ({
 "@type": "ListItem",
 position: i + 1,
 item: {
 "@type": providerSchemaType(p.specialty),
 name: p.name,
 medicalSpecialty: p.specialty,
 address: {
 "@type": "PostalAddress",
 streetAddress: p.address || undefined,
 addressLocality: cityShort,
 addressRegion: stateShort,
 addressCountry: "US",
 },
 ...(p.phone ? { telephone: p.phone } : {}),
 url: `${SITE}/providers/${buildProviderSlug(p)}`,
 },
 })),
 };

 const collection: Record<string, unknown> = {
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 numberOfItems: count,
 about: { "@type": "MedicalSpecialty", name: specialty },
 areaServed: {
 "@type": "City",
 name: cityShort,
 ...(stateShort ? { containedInPlace: { "@type": "State", name: stateShort } } : {}),
 ...(facts ? { geo: { "@type": "GeoCoordinates", latitude: facts.lat, longitude: facts.lng } } : {}),
 },
 isPartOf: { "@id": `${SITE}/#website` },
 };

 const breadcrumbs = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Specialties", item: `${SITE}/specialties` },
 { "@type": "ListItem", position: 3, name: specialty, item: `${SITE}/specialties/${params.specialty}` },
 { "@type": "ListItem", position: 4, name: `${specialty} in ${cityShort}`, item: url },
 ],
 };

 const faqs = buildFaqs({ specialty, city });
 const faqSchema = faqs.length > 0 ? {
   "@context": "https://schema.org",
   "@type": "FAQPage",
   mainEntity: faqs.map((f) => ({
     "@type": "Question",
     name: f.q,
     acceptedAnswer: { "@type": "Answer", text: f.a },
   })),
 } : null;

 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: canonical },
 { property: "og:type", content: "website" },
 robotsMeta(robots.robots),
 ],
 links: [{ rel: "canonical", href: canonical }],
 scripts: [
 { type: "application/ld+json", children: JSON.stringify(collection) },
 { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
 ...(providers.length > 0
 ? [{ type: "application/ld+json", children: JSON.stringify(itemList) }]
 : []),
 ...(faqSchema
 ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
 : []),
 ],
 };
 },
 component: SpecialtyCityPage,
 notFoundComponent: () => (
 <div className="min-h-screen flex items-center justify-center">
 <div className="text-center">
 <h1 className="text-2xl font-semibold mb-2">Page not found</h1>
 <Link to="/browse" className="text-primary underline">Browse providers</Link>
 </div>
 </div>
 ),
 errorComponent: ({ error }) => (
 <div className="min-h-screen flex items-center justify-center">
 <p className="text-muted-foreground">{error.message}</p>
 </div>
 ),
});

function SpecialtyCityPage() {
 const { specialty, city, providers } = Route.useLoaderData();
 const facts = CITY_FACTS[city];
 const cityShort = facts?.short ?? city.split(",")[0];
 const stateShort = facts?.state ?? (city.split(",")[1] ?? "").trim();
 const specialtyDisplay = specialtyPlural(specialty);
 const pluralLower = specialtyPluralLower(specialty);
 const withArticle = specialtyWithArticle(specialty);
 const count = providers.length;
 const stateRecord = stateFromNameSlug(stateShort);
 const stateNameSlugForLink = stateRecord ? stateNameSlug(stateRecord.name) : stateShort.toLowerCase();

 const insuranceCards = SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id)).filter(Boolean);
 const relatedSpecialties = SEO_SPECIALTIES.filter((s) => s !== specialty).slice(0, 16);
 const nearbyCities = pickNearbyCities({
   specialty,
   currentCity: city,
   allCities: SEO_CITIES,
   count: 10,
 });

 const intro = buildIntroParagraph({ specialty, city, providerCount: count });
 const faqs = buildFaqs({ specialty, city });

  const { h1 } = buildSpecialtyCityMeta({
    specialty, city, providerCount: count, nearbyCount: nearbyCities.length, faqCount: faqs.length,
  });

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="container mx-auto px-4 py-12 max-w-5xl">
 <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4">
 <Link to="/" className="hover:text-primary">Home</Link>
 <span className="mx-2">/</span>
 <Link to="/specialties" className="hover:text-primary">Specialties</Link>
 <span className="mx-2">/</span>
 <Link to="/locations" className="hover:text-primary">Locations</Link>
 <span className="mx-2">/</span>
 <span className="text-foreground">{specialtyDisplay} in {cityShort}</span>
 </nav>

 <header className="mb-8">
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">{h1}</h1>
 <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">{intro}</p>
 <div className="mt-5 flex flex-wrap gap-3">
 <Button asChild>
 <Link to="/search" search={{ specialty, location: city }}>
 <Search className="h-4 w-4 mr-2" />
 Refine search
 </Link>
 </Button>
 </div>
 </header>

 <section className="mb-12" aria-labelledby="providers-heading">
 <h2 id="providers-heading" className="text-2xl font-semibold mb-4">
 {count} {pluralLower} in {cityShort}
 </h2>
 <ul className="space-y-3">
 {providers.map((p: any) => (
 <li key={p.id}><ProviderCard p={p} /></li>
 ))}
 </ul>
 </section>

 <section className="mb-12" aria-labelledby="insurance-heading">
 <h2 id="insurance-heading" className="text-2xl font-semibold mb-4">
 {specialtyDisplay} in {cityShort} by insurance
 </h2>
 <p className="text-sm text-muted-foreground mb-4">
 These providers list this insurance as accepted, but coverage should be confirmed
 directly with the provider before booking.
 </p>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
 {insuranceCards.map((ins) => (
 <a
 key={ins!.id}
 href={`/insurance/${ins!.id}/${citySlug(city)}`}
 className="px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition text-sm text-center"
 >
 {ins!.short}
 </a>
 ))}
 </div>
 </section>

 <section className="mb-12" aria-labelledby="nearby-heading">
 <h2 id="nearby-heading" className="text-2xl font-semibold mb-4">
 {specialtyDisplay} in nearby cities
 </h2>
 <p className="text-sm text-muted-foreground mb-4">
 Same specialty in cities near {cityShort} — useful if your insurance network extends across the {stateShort ? `${stateShort} ` : ""}metro.
 </p>
 <div className="flex flex-wrap gap-2">
 {nearbyCities.map((c) => {
 const f = CITY_FACTS[c];
 const short = f?.short ?? c.split(",")[0];
 if (isLandingCity(c, SEO_CITIES)) {
 return (
 <Link
 key={c}
 to="/specialty/$specialty/$city"
 params={{ specialty: specialtySlug(specialty), city: citySlug(c) }}
 className="px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition text-sm"
 >
 {specialtyDisplay} in {short}
 </Link>
 );
 }
 return (
 <Link
 key={c}
 to="/search"
 search={{ specialty, location: c }}
 className="px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition text-sm"
 >
 {specialtyDisplay} in {short}
 </Link>
 );
 })}
 {stateShort && (
 <Link
 to="/specialty/$specialty/doctors/$state"
 params={{ specialty: specialtySlug(specialty), state: stateNameSlugForLink }}
 className="px-3 py-2 rounded-lg border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition text-sm font-medium"
 >
 All {specialtyDisplay} in {stateShort} →
 </Link>
 )}
 </div>
 </section>

 <section className="mb-12" aria-labelledby="about-heading">
 <h2 id="about-heading" className="text-2xl font-semibold mb-4">
 About finding {withArticle} in {cityShort}
 </h2>
 <div className="prose prose-neutral max-w-none text-foreground/90 leading-relaxed space-y-4">
 <p>
 {cityShort} has {pluralLower} practicing across hospital systems, group practices,
 and independent clinics{facts?.systems?.length ? `, including ${facts.systems.slice(0, 3).join(", ")}` : ""}.
 When choosing {withArticle} in {cityShort}, the three factors most patients care about are:{" "}
 <strong>insurance acceptance</strong>, <strong>distance from home or work</strong>, and{" "}
 <strong>patient reviews</strong>. Heallexa shows all three on every listing so you can compare
 without clicking back and forth.
 </p>
 <p>
 Many {cityShort} {pluralLower} list major insurance plans including Blue Cross Blue Shield, Aetna,
 UnitedHealthcare, Cigna, Humana, and Medicare. Listings reflect what providers have indicated they
 accept — always confirm in-network status directly with the office before booking. Out-of-network
 visits can cost 3–5× more than in-network ones.
 </p>
 </div>
 </section>

 <section className="mb-12" aria-labelledby="faq-heading">
 <h2 id="faq-heading" className="text-2xl font-semibold mb-4">Common questions about {pluralLower} in {cityShort}</h2>
 <div className="divide-y divide-border border border-border bg-card rounded-lg">
 {faqs.map((f) => (
 <details key={f.q} className="group p-5">
 <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between items-center">
 {f.q}
 <span className="text-muted-foreground group-open:rotate-45 transition-transform">+</span>
 </summary>
 <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
 </details>
 ))}
 </div>
 </section>

 <section>
 <h2 className="text-2xl font-semibold mb-4">Other specialties in {cityShort}</h2>
 <div className="flex flex-wrap gap-2">
 {relatedSpecialties.map((sp) => (
 <Link
 key={sp}
 to="/specialty/$specialty/$city"
 params={{ specialty: specialtySlug(sp), city: citySlug(city) }}
 className="px-3 py-2 rounded-full border border-border hover:border-primary hover:bg-primary/5 transition text-sm"
 >
 {specialtyPlural(sp)}
 </Link>
 ))}
 </div>
 </section>
 </main>
 <FooterSection />
 </div>
 );
}
